import { useEffect, useState } from 'react';

export const isIosDevice = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export const isAppInstalled = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const detectPlatform = () => {
  const ua = window.navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

const trackPwaEvent = (type) => {
  const token = localStorage.getItem('cardioai_token');
  fetch(`${API}/pwa/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ type, platform: detectPlatform() })
  }).catch(() => {});
};

// Report the install once per device, and each app-mode launch once per day
export const reportPwaUsage = () => {
  if (!isAppInstalled()) return;

  if (!localStorage.getItem('pb_install_reported')) {
    localStorage.setItem('pb_install_reported', '1');
    trackPwaEvent('install');
  }

  const today = new Date().toISOString().slice(0, 10);
  if (localStorage.getItem('pb_launch_reported') !== today) {
    localStorage.setItem('pb_launch_reported', today);
    trackPwaEvent('launch');
  }
};

// Chrome fires beforeinstallprompt once, so it is kept here for every consumer to reuse
let savedPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    savedPrompt = e;
    window.dispatchEvent(new CustomEvent('pb-install-available'));
  });
}

export const usePwaInstall = () => {
  const [canInstall, setCanInstall] = useState(!!savedPrompt);
  const [installed, setInstalled] = useState(isAppInstalled());

  useEffect(() => {
    const onAvailable = () => setCanInstall(true);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      savedPrompt = null;
      // Count the install right away, even before the app is reopened in app mode
      if (!localStorage.getItem('pb_install_reported')) {
        localStorage.setItem('pb_install_reported', '1');
        trackPwaEvent('install');
      }
    };
    window.addEventListener('pb-install-available', onAvailable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('pb-install-available', onAvailable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!savedPrompt) return false;
    savedPrompt.prompt();
    const { outcome } = await savedPrompt.userChoice;
    savedPrompt = null;
    setCanInstall(false);
    return outcome === 'accepted';
  };

  return { canInstall, installed, isIos: isIosDevice(), promptInstall };
};

export default usePwaInstall;
